import { http } from "./client";

// Performance evaluation: templates (form builder). Every function returns the response body.
const data = (r) => r.data;

/** Drop empty filters so they aren't sent as `status=` etc. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

export const templateApi = {
  // GET /performance/templates?status=&evaluationType=&code= → Template[] (by code, newest version first)
  getAll: (filters = {}) =>
    http.get("/performance/templates", { params: clean(filters) }).then(data),
  getById: (id) => http.get(`/performance/templates/${id}`).then(data),
  // Creates DRAFT v1. 409 if the code exists.
  create: (body) => http.post("/performance/templates", body).then(data),
  // DRAFT only (409 otherwise); `code` can't change
  update: (id, body) =>
    http.patch(`/performance/templates/${id}`, body).then(data),
  // Copies into a new DRAFT (v+1). 409 if that code already has a draft.
  newVersion: (id) =>
    http.post(`/performance/templates/${id}/new-version`).then(data),
  // DRAFT → ACTIVE; the previous ACTIVE of the same code is archived
  activate: (id) =>
    http.post(`/performance/templates/${id}/activate`).then(data),
  // ACTIVE → ARCHIVED (no new evaluations)
  archive: (id) => http.post(`/performance/templates/${id}/archive`).then(data),
  // Unused DRAFT only, 204
  remove: (id) => http.delete(`/performance/templates/${id}`).then(() => id),
};

export const evaluationApi = {
  /**
   * GET /performance/evaluations → { data, total, page, limit } (newest first)
   * filters: status (incl. CONTESTED), employeeId, evaluatorEmployeeId, templateCode, branchId, departmentId,
   *          ratingBand, minScore, maxScore, employeeResponse (ACCEPT|CONTEST), search (prefix), year, page, limit
   */
  getList: (filters = {}) =>
    http.get("/performance/evaluations", { params: clean(filters) }).then(data),
  // Same filters minus status/page/limit → { total, byStatus: { PENDING_SELF: n, ... } }
  getCounts: (filters = {}) =>
    http
      .get("/performance/evaluations/counts", { params: clean(filters) })
      .then(data),
  // → EvaluationDetails (includes `permissions`)
  getById: (id) => http.get(`/performance/evaluations/${id}`).then(data),
  // { employeeId, evaluatorEmployeeId? (omit = the employee's level-1 approver), templateId?, periodLabel, periodStart, periodEnd, dueDate? }
  // 409 when the employee already has the same form for an overlapping period
  create: (body) =>
    http.post("/performance/evaluations", clean(body)).then(data),
  // PENDING_SELF / PENDING_EVALUATION only
  changeEvaluator: (id, evaluatorEmployeeId) =>
    http
      .patch(`/performance/evaluations/${id}/evaluator`, {
        evaluatorEmployeeId,
      })
      .then(data),
  // reason required (max 500)
  cancel: (id, reason) =>
    http.post(`/performance/evaluations/${id}/cancel`, { reason }).then(data),
  // Replaces the whole `responses` object; returns details with recomputed scores
  saveResponses: (id, responses) =>
    http
      .put(`/performance/evaluations/${id}/responses`, { responses })
      .then(data),
  submit: (id) => http.post(`/performance/evaluations/${id}/submit`).then(data),
  // HR: { action: UPHOLD (→ COMPLETED) | REEVALUATE (→ back to the evaluator), remarks }
  resolveContest: (id, { action, remarks }) =>
    http
      .post(`/performance/evaluations/${id}/resolve-contest`, {
        action,
        remarks,
      })
      .then(data),
  // GET /performance/evaluations/reports/summary?year&templateCode&branchId&departmentId
  getReportSummary: (filters = {}) =>
    http
      .get("/performance/evaluations/reports/summary", {
        params: clean(filters),
      })
      .then(data),
  // ACTIVE templates that fit the employee, best first (the server's default pick is the first)
  suggestTemplates: (employeeId) =>
    http
      .get("/performance/templates/suggest", { params: { employeeId } })
      .then(data),
};
